import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import {
  canNavigateTo,
  dispatchSidebarNavigate,
} from "../../utils/sidebarNavigation";

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path, includeNested = false) => {
    const current = location.pathname;
    if (current === path) return true;
    if (!includeNested || path === "/") return false;
    return current.startsWith(`${path}/`);
  };

  const isRentalsActive = () => {
    const current = location.pathname;
    if (current === "/rentals") return true;
    return (
      current.startsWith("/rentals/") && !current.startsWith("/rentals/management")
    );
  };

  const GuardedLink = ({ to, onClick, children, ...props }) => {
    const handleClick = (event) => {
      event.preventDefault();
      setIsOpen(false);
      const target = typeof to === "string" ? to : to?.pathname || "/";
      if (!canNavigateTo(target)) {
        return;
      }
      onClick?.(event);
      dispatchSidebarNavigate();
      navigate(to);
    };

    return (
      <Link to={to} onClick={handleClick} {...props}>
        {children}
      </Link>
    );
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    dispatchSidebarNavigate();
  };

  const linkClass = (active) =>
    `flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${
      active ? "text-white bg-primary" : "text-gray-700 hover:bg-gray-100"
    }`;

  const NavSection = ({ title, children }) => (
    <div className="mt-3">
      <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
        {title}
      </p>
      {children}
    </div>
  );

  const NavItem = ({ to, icon, label, active, onClick }) => (
    <GuardedLink
      to={to}
      onClick={onClick || handleLinkClick}
      className={linkClass(active)}
    >
      <span className="material-icons-outlined mr-2 text-sm">
        <i className={icon} />
      </span>
      <span>{label}</span>
    </GuardedLink>
  );

  const sidebarPortal = createPortal(
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-[190] bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`
        pointer-events-auto fixed left-0 top-0 z-[200] flex h-screen w-64 flex-col border-r border-gray-200 bg-white
        transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${isOpen ? "translate-x-0" : "-translate-x-full"}
      `}
      >
        {/* Logo and Settings */}
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <img src={logo} alt="logo" className="w-28 h-auto" />
            <GuardedLink
              to="/settings"
              onClick={handleLinkClick}
              className={`p-2 rounded-md transition-colors ml-auto ${
                isActive("/settings")
                  ? "text-primary bg-primary/10"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              title="Settings"
            >
              <i className="fi fi-rr-settings text-lg"></i>
            </GuardedLink>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
          >
            {/* <span className="material-icons-outlined text-xs">close</span> */}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 overflow-y-auto h-[calc(100vh-64px)]">
          <NavItem
            to="/"
            icon="fi fi-rr-apps"
            label="Dashboard"
            active={isActive("/")}
            onClick={handleLinkClick}
          />

          <NavSection title="Registration">
            <NavItem
              to="/suppliers"
              icon="fi fi-rr-users-alt"
              label="Suppliers"
              active={isActive("/suppliers")}
            />
            <NavItem
              to="/users"
              icon="fi fi-rr-user"
              label="Users"
              active={isActive("/users")}
            />
          </NavSection>

          <NavSection title="Packages">
            <NavItem
              to="/packages"
              icon="fi fi-rr-backpack"
              label="Packages"
              active={isActive("/packages", true)}
            />
            <NavItem
              to="/enquiry"
              icon="fi fi-rr-envelope"
              label="Enquiries"
              active={isActive("/enquiry")}
            />
          </NavSection>

          <NavSection title="Rentals">
            <NavItem
              to="/rentals"
              icon="fi fi-rr-car"
              label="Rentals"
              active={isRentalsActive()}
            />
            <NavItem
              to="/rentals/management"
              icon="fi fi-rr-clipboard-list"
              label="Rental management"
              active={isActive("/rentals/management")}
            />
          </NavSection>

          <NavSection title="Events">
            <NavItem
              to="/events"
              icon="fi fi-rr-calendar"
              label="Events"
              active={isActive("/events", true)}
            />
            <NavItem
              to="/event-category"
              icon="fi fi-rr-tags"
              label="Event category"
              active={isActive("/event-category")}
            />
          </NavSection>

          <NavSection title="Attractions">
            <NavItem
              to="/attractions"
              icon="fi fi-rr-mountain"
              label="Attractions"
              active={isActive("/attractions", true)}
            />
          </NavSection>

            <NavSection title="Activities">
            <NavItem
              to="/supplier-activities"
              icon="fi fi-rr-hiking"
              label="Activities"
              active={isActive("/supplier-activities")}
            />
            <NavItem
              to="/activities"
              icon="fi fi-rr-list"
              label="Activity master"
              active={isActive("/activities")}
            />
          </NavSection>

          <NavSection title="Bookings">
            <NavItem
              to="/bookings"
              icon="fi fi-rr-ticket"
              label="Bookings"
              active={isActive("/bookings", true)}
            />
            <NavItem
              to="/transactions"
              icon="fi fi-rr-exchange"
              label="Transactions"
              active={isActive("/transactions")}
            />
          </NavSection>

          <NavSection title="Others">
            <NavItem
              to="/reviews"
              icon="fi fi-rr-star"
              label="User reviews"
              active={isActive("/reviews")}
            />
          </NavSection>

          <NavSection title="General">
            <NavItem
              to="/countries"
              icon="fi fi-rr-globe"
              label="Countries"
              active={isActive("/countries")}
            />
            <NavItem
              to="/states"
              icon="fi fi-rr-map"
              label="States"
              active={isActive("/states")}
            />
            <NavItem
              to="/destinations"
              icon="fi fi-rs-map-pin"
              label="Destinations"
              active={isActive("/destinations")}
            />
            <NavItem
              to="/meals"
              icon="fi fi-rr-restaurant"
              label="Meals"
              active={isActive("/meals")}
            />
            <NavItem
              to="/inclusions"
              icon="fi fi-rr-list"
              label="Inclusions"
              active={isActive("/inclusions")}
            />
            <NavItem
              to="/suitable"
              icon="fi fi-rr-heart"
              label="Suitable For"
              active={isActive("/suitable")}
            />
            <NavItem
              to="/activity-category"
              icon="fi fi-rr-tags"
              label="Activity category"
              active={isActive("/activity-category")}
            />
            <NavItem
              to="/attraction-category"
              icon="fi fi-rr-tags"
              label="Attraction category"
              active={isActive("/attraction-category")}
            />
            <NavItem
              to="/language"
              icon="fi fi-rr-language"
              label="Languages"
              active={isActive("/language")}
            />
          </NavSection>
        </nav>
      </aside>
    </>,
    document.body
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-4 top-3 z-[210] -mt-1 ml-[3px] rounded-md bg-white p-1 shadow-sm transition-colors duration-200 hover:bg-gray-50 lg:hidden"
      >
        <span className="material-icons-outlined text-[10px] text-gray-600 mt-0.5">
          {isOpen ? (
            <i className="fi fi-rr-cross mb-[2%]"></i>
          ) : (
            <i className="fi fi-rr-menu-burger mb-[2%]"></i>
          )}
        </span>
      </button>
      {sidebarPortal}
    </>
  );
};

export default Sidebar;
